import { Space, Tooltip, Button, Typography } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import type { ColumnType } from 'antd/es/table'
import type { TFunction } from 'i18next'

import type { ReturnRoot } from '@repo/sdk'

import { ProductCell } from '../../components/ProductCell'
import { StatusBadge, type StatusConfig } from '../../components/StatusBadge'
import { UserAvatar } from '../../components/UserAvatar'
import { NicknameText } from '../../components/NicknameText'

import type { ReturnStatus } from './returnStatusConfig'
import type { ReturnReason } from './returnReasonConfig'
import { formatOrderBriefLabel } from '../orders/orderDisplay'
import { formatKstDateTime } from '../../utils/formatKstDateTime'

type ReturnStatusConfigMap = Record<ReturnStatus, StatusConfig>

export interface ReturnListColumnProps {
  onViewDetail: (returnId: string, orderId: string) => void
  t: TFunction
  statusConfig: ReturnStatusConfigMap
  reasonLabels: Record<ReturnReason, string>
}

export function createReturnListColumns({
  onViewDetail,
  t,
  statusConfig,
  reasonLabels,
}: ReturnListColumnProps): ColumnType<ReturnRoot>[] {
  return [
    {
      title: t('common.columns.returnId'),
      dataIndex: 'returnId',
      key: 'returnId',
      width: 120,
      render: (_: unknown, record: ReturnRoot) => (
        <Tooltip title={record.returnId}>
          <Button
            data-testid={`return-list-id-link-${record.returnId}`}
            type="link"
            size="small"
            onClick={() => onViewDetail(record.returnId, record.order.orderId)}
          >
            {record.returnId.slice(0, 8)}...
          </Button>
        </Tooltip>
      ),
    },
    {
      title: t('common.columns.order'),
      key: 'orderId',
      width: 300,
      render: (_: unknown, record: ReturnRoot) => (
        <Typography.Text>{formatOrderBriefLabel(record.order)}</Typography.Text>
      ),
    },
    {
      title: t('common.columns.productName'),
      key: 'product',
      width: 200,
      render: (_: unknown, record: ReturnRoot) => (
        <ProductCell
          name={record.returnItems[0]?.productName}
          thumbnailUrl={record.returnItems[0]?.productThumbnailUrl}
        />
      ),
    },
    {
      title: t('common.columns.buyer'),
      key: 'buyer',
      width: 150,
      render: (_: unknown, record: ReturnRoot) => (
        <Space>
          <UserAvatar
            src={record.buyer.profileImageUrl ?? undefined}
            nickname={record.buyer.nickname}
            size="small"
          />
          <NicknameText nickname={record.buyer.nickname} />
        </Space>
      ),
    },
    {
      title: t('common.columns.returnReason'),
      key: 'returnReason',
      width: 150,
      ellipsis: true,
      render: (_: unknown, record: ReturnRoot) => {
        const label = reasonLabels[record.returnReason as ReturnReason] ?? record.returnReason
        return (
          <Tooltip title={label}>
            <Typography.Text ellipsis>{label}</Typography.Text>
          </Tooltip>
        )
      },
    },
    {
      title: t('common.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (_: unknown, record: ReturnRoot) => (
        <StatusBadge status={record.status} configMap={statusConfig} />
      ),
    },
    {
      title: t('common.columns.requestDate'),
      dataIndex: 'requestedAt',
      key: 'requestedAt',
      width: 150,
      render: (value: string) => (value ? formatKstDateTime(value, 'YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: t('common.columns.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      render: (_: unknown, record: ReturnRoot) => (
        <Tooltip title={t('common.buttons.view')}>
          <Button
            data-testid={`return-list-view-btn-${record.returnId}`}
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onViewDetail(record.returnId, record.order.orderId)}
          />
        </Tooltip>
      ),
    },
  ]
}
